export class PinchZoom {
    constructor(element) {
        this.el = element;
        this.scale = 1;
        this.initialDistance = 0;
        this.translateX = 0;
        this.translateY = 0;
        this.startX = 0;
        this.startY = 0;
        this.isDragging = false;
        this.isMinimapDragging = false;

        // Create minimap
        this.minimap = document.createElement('div');
        this.minimap.className = 'minimap';
        this.el.parentNode.appendChild(this.minimap);

        // Wait for the image to load before updating the minimap
        if (this.el.tagName === 'IMG' && !this.el.complete) {
            this.el.addEventListener('load', () => {
                this.updateMinimap(this.scale, this.translateX, this.translateY);
            });
        } else {
            this.updateMinimap(this.scale, this.translateX, this.translateY);
        }

        this.bindEvents();
    }

    destroy() {
        this.el.removeEventListener('touchstart', this.onTouchStart.bind(this));
        this.el.removeEventListener('touchmove', this.onTouchMove.bind(this));
        this.el.removeEventListener('touchend', this.onTouchEnd.bind(this));
        this.el.removeEventListener('wheel', this.onWheel.bind(this));
        this.el.removeEventListener('mousedown', this.onMouseDown.bind(this));
        window.removeEventListener('mousemove', this.onMouseMove.bind(this));
        window.removeEventListener('mouseup', this.onMouseUp.bind(this));

        if (this.minimap && this.minimap.parentNode) {
            this.minimap.parentNode.removeChild(this.minimap);
        }

        this.el.style.transform = '';
    }

    bindEvents() {
        this.el.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
        this.el.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        this.el.addEventListener('touchend', this.onTouchEnd.bind(this));

        this.el.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

        this.el.addEventListener('mousedown', this.onMouseDown.bind(this));
        window.addEventListener('mousemove', this.onMouseMove.bind(this));
        window.addEventListener('mouseup', this.onMouseUp.bind(this));

        this.minimap.addEventListener('mousedown', this.onMinimapMouseDown.bind(this));
        window.addEventListener('mousemove', this.onMinimapMouseMove.bind(this));
        window.addEventListener('mouseup', this.onMinimapMouseUp.bind(this));
    }

    onTouchStart(e) {
        if (e.touches.length === 2) {
            this.initialDistance = this.getDistance(e.touches[0], e.touches[1]);
        } else if (e.touches.length === 1 && this.scale > 1) {
            this.isDragging = true;
            this.startX = e.touches[0].clientX - this.translateX;
            this.startY = e.touches[0].clientY - this.translateY;
        }
    }

    onTouchMove(e) {
        if (e.touches.length === 2) {
            e.preventDefault();
            const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
            const delta = currentDistance / this.initialDistance;
            let newScale = this.scale * delta;

            newScale = Math.max(1, Math.min(newScale, 4));
            this.updateTransform(newScale, this.translateX, this.translateY);
        } else if (e.touches.length === 1 && this.isDragging) {
            e.preventDefault();
            this.translateX = e.touches[0].clientX - this.startX;
            this.translateY = e.touches[0].clientY - this.startY;
            this.limitTranslation();
            this.updateTransform(this.scale, this.translateX, this.translateY);
        }
    }

    onTouchEnd(e) {
        if (e.touches.length < 2) {
            const transform = window.getComputedStyle(this.el).transform;
            if (transform !== 'none') {
                const matrix = new DOMMatrix(transform);
                this.scale = matrix.a;
            }
        }
        if (e.touches.length === 0) {
            this.isDragging = false;
        }
    }

    onMouseDown(e) {
        if (this.scale > 1) {
            this.isDragging = true;
            this.startX = e.clientX - this.translateX;
            this.startY = e.clientY - this.translateY;
        }
    }

    onMouseMove(e) {
        if (this.isDragging) {
            this.translateX = e.clientX - this.startX;
            this.translateY = e.clientY - this.startY;
            this.limitTranslation();
            this.updateTransform(this.scale, this.translateX, this.translateY);
        }
    }

    onMouseUp() {
        this.isDragging = false;
    }

    onMinimapMouseDown(e) {
        this.isMinimapDragging = true;
        this.startX = e.clientX;
        this.startY = e.clientY;
    }
    onMinimapMouseMove(e) {
        if (this.isMinimapDragging) {
            const deltaX = e.clientX - this.startX;
            const deltaY = e.clientY - this.startY;
    
            const rect = this.minimap.getBoundingClientRect();
    
            this.translateX -= deltaX * (this.el.clientWidth / rect.width);
            this.translateY -= deltaY * (this.el.clientHeight / rect.height);
            this.limitTranslation();
            this.updateTransform(this.scale, this.translateX, this.translateY);
    
            this.startX = e.clientX;
            this.startY = e.clientY;
        }
    }

    onMinimapMouseUp() {
        this.isMinimapDragging = false;
    }

    onWheel(e) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        this.scale *= delta;
        this.scale = Math.max(1, Math.min(this.scale, 4));
        this.limitTranslation();
        this.updateTransform(this.scale, this.translateX, this.translateY);
    }

    getDistance(touch1, touch2) {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.hypot(dx, dy);
    }

    limitTranslation() {
        const rect = this.el.getBoundingClientRect();
        const scaledWidth = rect.width * this.scale;
        const scaledHeight = rect.height * this.scale;

        const maxTranslateX = (scaledWidth - rect.width) / 2;
        const maxTranslateY = (scaledHeight - rect.height) / 2;

        this.translateX = Math.min(maxTranslateX, Math.max(-maxTranslateX, this.translateX));
        this.translateY = Math.min(maxTranslateY, Math.max(-maxTranslateY, this.translateY));
    }

    updateTransform(scale, translateX, translateY) {
        if (scale === 1) {
            translateX = 0;
            translateY = 0;
        }

        this.el.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
        this.updateMinimap(scale, translateX, translateY);
    }

    updateMinimap(scale, translateX, translateY) {
        const rect = this.el.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        if (scale <= 1) {
            // this.minimap.style.display = 'none';
            // return;
        }

        this.minimap.style.display = 'block';

        const viewportW = width / scale;
        const viewportH = height / scale;

        const offsetX = (-translateX + (width * (scale - 1)) / 2) / (width * scale) * 100;
        const offsetY = (-translateY + (height * (scale - 1)) / 2) / (height * scale) * 100;

        this.minimap.style.backgroundImage = `url(${this.el.src || this.el.style.backgroundImage.replace(/url$begin:math:text$["']?|["']?$end:math:text$/g, '')})`;

        this.minimap.innerHTML = `
            <div class="viewport" 
                style="
                    width: ${viewportW / width * 100}%;
                    height: ${viewportH / height * 100}%;
                    left: ${offsetX}%;
                    top: ${offsetY}%;
                ">
            </div>
        `;
    }
}